<template>
  <div class="people-management">
    <h2>Gestione Persone</h2>
    
    <!-- Loading state -->
    <div v-if="isLoading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Caricamento...</span>
      </div>
    </div>

    <!-- Error display -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Success message -->
    <div v-if="successMessage" class="alert alert-success" role="alert">
      {{ successMessage }}
    </div>

    <!-- Header con pulsante Nuova Persona -->
    <div class="d-flex justify-content-between align-items-center mb-4" v-if="!isLoading">
      <h4>Lista Persone ({{ people.length }})</h4>
      <button 
        class="btn btn-primary"
        @click="openNewPersonDialog"
        title="Aggiungi nuova persona"
      >
        Nuova Persona
      </button>
    </div>

    <!-- Lista persone -->
    <div v-if="!isLoading">
      <div v-if="people.length === 0" class="card">
        <div class="card-body text-muted text-center py-4">
          Nessuna persona trovata. Clicca su "Nuova Persona" per creare la prima.
        </div>
      </div>
      
      <TableSortable v-else :data="peopleWithFullName" :options="peopleTableOptions">
        <template #field-id_people="{ row }">
          {{ row.id_people }}
        </template>
        
        <template #field-full_name="{ row }">
          <router-link :to="`/people/${row.id_people}`" class="text-decoration-none">
            <strong>{{ row.full_name }}</strong>
          </router-link>
        </template>
        
        <template #field-email="{ row }">
          {{ row.email }}
        </template>
        
        <template #field-updated_timestamp="{ row }">
          {{ formatDate(row.updated_timestamp) }}
        </template>
        
        <template #field-actions="{ row }">
          <div class="btn-group" role="group">
            <button 
              class="btn btn-sm btn-outline-success"
              @click="goToPersonDetail(row.id_people)"
              title="Dettaglio e Budget Anni"
            >
              <i class="fas fa-user-chart"></i> Dettaglio
            </button>
            <button 
              class="btn btn-sm btn-outline-primary"
              @click="openEditPersonDialog(row)"
              title="Modifica"
            >
              Modifica
            </button>
            <button 
              class="btn btn-sm btn-outline-danger"
              @click="deletePerson(row.id_people)"
              title="Elimina"
            >
              Elimina
            </button>
          </div>
        </template>
      </TableSortable>
    </div>

    <!-- Dialog Modal per Persona -->
    <div 
      v-if="showDialog" 
      class="modal fade show" 
      style="display: block; background-color: rgba(0,0,0,0.5);"
      tabindex="-1" 
      role="dialog"
      @click="handleModalClick"
    >
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingId ? 'Modifica Persona' : 'Nuova Persona' }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeDialog"
              aria-label="Chiudi"
            ></button>
          </div>
          
          <div class="modal-body">
            <!-- Error nel dialog -->
            <div v-if="dialogError" class="alert alert-danger">
              {{ dialogError }}
            </div>

            <!-- Form DBMng universale -->
            <DbmngForm
              v-model="formData"
              :dbmng-schema="dbmngSchema"
              :show-actions="false"
              :validate-on-change="true"
              @validate="onFormValidate"
            />
          </div>
          
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeDialog"
              :disabled="isDialogLoading"
            >
              Annulla
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="savePerson"
              :disabled="isDialogLoading"
            >
              <span v-if="isDialogLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingId ? 'Salva Modifiche' : 'Crea Persona' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DbmngForm from '@/lib/DbmngForm.vue'
import TableSortable from '@/components/TableSortable.vue'
import { useAppStore } from '@/stores/app.js'

export default {
  name: 'PeopleManagement',
  components: {
    DbmngForm,
    TableSortable
  },
  setup() {
    const store = useAppStore()
    return { store }
  },
  data() {
    return {
      people: [],
      dbmngSchema: null,
      formData: {},
      editingId: null,
      isLoading: false,
      error: null,
      successMessage: null,
      // Dialog state
      showDialog: false,
      isDialogLoading: false,
      dialogError: null
    }
  },
  
  mounted() {
    this.loadPeople()
  },
  
  methods: {
    /**
     * Naviga al dettaglio della persona per gestire budget anni
     */
    goToPersonDetail(personId) {
      this.$router.push(`/people/${personId}`)
    },
    
    async loadPeople() {
      try {
        this.isLoading = true
        this.error = null
        
        const result = await this.store.apiCall('/people/people', { method: 'GET' })
        
        if (result.ok) {
          this.people = result.data || []
          this.dbmngSchema = result.dbmng
          this.initializeFormData()
        } else {
          this.error = result.message || 'Errore nel caricamento delle persone'
        }
      } catch (error) {
        this.error = error.message || 'Errore di connessione'
        console.error('Errore loadPeople:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    initializeFormData() {
      // Inizializza formData con campi vuoti basati su schema DBMng
      this.formData = {};
      // if (this.dbmngSchema?.fields) {
      //   Object.keys(this.dbmngSchema.fields).forEach(fieldName => {
      //     this.formData[fieldName] = ''
      //   })
      // }
    },
    
    async savePerson() {
      try {
        this.isDialogLoading = true
        this.dialogError = null
        this.error = null
        this.successMessage = null
        
        let result
        
        if (this.editingId) {
          // Update esistente
          result = await this.store.apiCall(`/people/people/${this.editingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.formData)
          })
          this.successMessage = 'Persona aggiornata con successo'
        } else {
          // Nuova persona
          result = await this.store.apiCall('/people/people', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.formData)
          })
          this.successMessage = 'Persona creata con successo'
        }
        
        if (result.ok) {
          this.closeDialog()
          this.loadPeople() // Ricarica la lista
        } else {
          this.dialogError = result.message || 'Errore nel salvataggio'
        }
      } catch (error) {
        this.dialogError = error.message || 'Errore di connessione'
        console.error('Errore savePerson:', error)
      } finally {
        this.isDialogLoading = false
      }
    },

    openNewPersonDialog() {
      this.editingId = null
      this.initializeFormData()
      this.dialogError = null
      this.showDialog = true
    },

    openEditPersonDialog(person) {
      this.editingId = person.id_people
      
      this.formData = person;
      
      this.dialogError = null
      this.showDialog = true
    },

    closeDialog() {
      this.showDialog = false
      this.editingId = null
      this.dialogError = null
      this.isDialogLoading = false
      this.initializeFormData()
    },

    handleModalClick(event) {
      // Modale non si chiude quando si clicca fuori
      // if (event.target === event.currentTarget) {
      //   this.closeDialog()
      // }
    },
    
    editPerson(person) {
      // Metodo deprecated - ora usa openEditPersonDialog
      this.openEditPersonDialog(person)
    },
    
    async deletePerson(id) {
      if (!confirm('Sei sicuro di voler eliminare questa persona?')) {
        return
      }
      
      try {
        this.isLoading = true
        this.error = null
        this.successMessage = null
        
        const result = await this.store.apiCall(`/people/people/${id}`, { method: 'DELETE' })
        
        if (result.ok) {
          this.successMessage = 'Persona eliminata con successo'
          this.loadPeople() // Ricarica la lista
        } else {
          this.error = result.message || 'Errore nell\'eliminazione'
        }
      } catch (error) {
        this.error = error.message || 'Errore di connessione'
        console.error('Errore deletePerson:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    resetForm() {
      this.editingId = null
      this.initializeFormData()
      this.error = null
      this.successMessage = null
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        return new Date(dateString).toLocaleString('it-IT')
      } catch (error) {
        return 'Data non valida'
      }
    }
  },
  
  computed: {
    peopleTableOptions() {
      return {
        structure: [
          { key: 'id_people', label: 'ID', sortable: true },
          { key: 'full_name', label: 'Nome Completo', sortable: true, filterable: true },
          { key: 'email', label: 'Email', sortable: true, filterable: true },
          { key: 'updated_timestamp', label: 'Ultima Modifica', sortable: true },
          { key: 'actions', label: 'Azioni', sortable: false }
        ]
      }
    },
    
    // Aggiungi nome completo ai dati
    peopleWithFullName() {
      return this.people.map(person => ({
        ...person,
        full_name: `${person.name} ${person.surname}`
      }))
    }
  }
}
</script>

<style scoped>
.people-management {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.btn-group .btn {
  margin-right: 5px;
}

.table th {
  background-color: #f8f9fa;
}

.card {
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

.form-label .text-danger {
  font-weight: bold;
}

.alert {
  margin-bottom: 1rem;
}

/* Dialog Modal Styles */
.modal.show {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-content {
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
  color: #495057;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #000;
  opacity: 0.5;
  cursor: pointer;
}

.btn-close:hover {
  opacity: 0.8;
}

.btn-close:before {
  content: '×';
}

@media (max-width: 768px) {
  .people-management {
    padding: 10px;
  }
  
  .d-flex {
    flex-direction: column;
  }
  
  .d-flex .btn {
    margin-bottom: 10px;
  }
}
</style>